import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getPageBySlug } from '@/api/wiki'
import { useWikiNav } from '@/components/layout/wikiNavContext'
import { buildPatchTimeline, extractPatchSections } from '@/utils/changelog'
import type { PatchInfo, PatchSection } from '@/utils/changelog'

/** Months (newest-first) shown expanded by default; older ones collapse. */
const EXPANDED_MONTHS = 2

/** Sticky-header offset used when scrolling to in-page anchors. */
const SCROLL_OFFSET = 96

function monthId(label: string): string {
  return 'month-' + label.toLowerCase().replace(/[^a-z0-9]+/g, '-')
}

export default function ChangelogHubPage() {
  const { pages, loading, error } = useWikiNav()
  const [showOlder, setShowOlder] = useState(false)

  const timeline = useMemo(() => buildPatchTimeline(pages), [pages])
  const latest = timeline[0] ?? null
  const rest = timeline.slice(1)

  // The pages list ships without content_html; fetch the latest patch in full
  // to offer jump chips into its sections. The spotlight renders without them
  // until (and unless) this resolves.
  const [latestSections, setLatestSections] = useState<PatchSection[]>([])
  const latestSlug = latest?.page.slug ?? null
  useEffect(() => {
    if (!latestSlug) return
    let cancelled = false
    getPageBySlug(latestSlug)
      .then(full => {
        if (cancelled) return
        setLatestSections(extractPatchSections(full.content_html).slice(0, 8))
      })
      .catch(() => { /* chips are optional; the CTA still opens the full notes */ })
    return () => { cancelled = true }
  }, [latestSlug])

  const monthGroups = useMemo(() => {
    const groups: { label: string; entries: PatchInfo[] }[] = []
    for (const entry of rest) {
      const last = groups[groups.length - 1]
      if (last && last.label === entry.monthLabel) last.entries.push(entry)
      else groups.push({ label: entry.monthLabel, entries: [entry] })
    }
    return groups
  }, [rest])

  const visibleGroups = monthGroups.slice(0, EXPANDED_MONTHS)
  const olderGroups = monthGroups.slice(EXPANDED_MONTHS)
  const olderCount = olderGroups.reduce((n, g) => n + g.entries.length, 0)

  function jumpToMonth(label: string, isCollapsed: boolean) {
    if (isCollapsed) setShowOlder(true)
    // Wait for the collapsed groups to render before measuring the target.
    setTimeout(() => {
      const el = document.getElementById(monthId(label))
      if (!el) return
      const y = window.scrollY + el.getBoundingClientRect().top - SCROLL_OFFSET
      window.scrollTo({ top: y, behavior: 'smooth' })
    }, 50)
  }

  if (loading) {
    return (
      <div className="panel p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-calamity-panel-hover rounded-lg w-1/3" />
          <div className="h-40 bg-calamity-panel-hover rounded-xl" />
          <div className="h-4 bg-calamity-panel-hover rounded w-3/4" />
          <div className="h-4 bg-calamity-panel-hover rounded w-2/3" />
        </div>
      </div>
    )
  }

  if (error || !latest) {
    return (
      <div className="panel p-8 text-center">
        <div className="text-5xl mb-4 opacity-50" aria-hidden="true">&#129534;</div>
        <h1 className="text-2xl font-display text-calamity-text mb-2">No Patch Notes Yet</h1>
        <p className="text-calamity-text-dim">Patch notes will appear here as updates ship.</p>
      </div>
    )
  }

  return (
    <div className="flex gap-4">
      <article className="panel wiki-readable-panel wiki-changelog-readable-panel p-6 sm:p-8 flex-1 min-w-0 animate-fade-up">
        <div className="wiki-article">
          <header className="wiki-page-header mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-calamity-panel-hover/60 border border-calamity-border-subtle/50 text-[22px] leading-none" aria-hidden="true">&#129534;</span>
              <h1 className="text-3xl sm:text-4xl font-sans font-semibold tracking-tight text-calamity-text leading-tight">
                Changelog
              </h1>
            </div>
            <p className="text-base text-calamity-text-dim mt-2">
              All game updates, newest first.
            </p>
          </header>

          {/* Latest patch spotlight */}
          <section
            aria-label="Latest patch"
            className="relative overflow-hidden rounded-xl border border-calamity-border/90 p-6 sm:p-7 mb-8
                       bg-[linear-gradient(135deg,rgba(132,74,203,0.30),transparent_46%),linear-gradient(90deg,rgba(48,40,82,0.98),rgba(26,21,48,0.86))]
                       shadow-[0_5px_20px_rgba(39,29,58,0.28),0_18px_54px_rgba(0,0,0,0.26)]"
          >
            <div className="inline-flex items-center gap-2 mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-[#ffb04a]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ffb04a]" aria-hidden="true" />
              Latest
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-calamity-text uppercase leading-none">
              {latest.version ? `Version ${latest.version.replace(/^v/i, '')}` : latest.page.title}
            </h2>
            <div className="mt-2 text-sm font-bold text-[#8b90ff]">{latest.dateLabel}</div>
            {latest.page.meta_description && (
              <p className="mt-3 max-w-[62ch] text-[15px] leading-relaxed text-calamity-text-muted">
                {latest.page.meta_description}
              </p>
            )}

            {latestSections.length > 1 && (
              <div className="mt-4 flex flex-wrap gap-2" aria-label="Patch sections">
                {latestSections.map(section => (
                  <Link
                    key={section.id}
                    to={`/${latest.page.slug}#${section.id}`}
                    className="rounded-full border border-[#8b90ff]/30 bg-[#8b90ff]/10 px-3 py-1 text-xs font-semibold text-calamity-text
                               transition-colors hover:bg-[#8b90ff]/20
                               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-calamity-accent/50"
                  >
                    {section.text}
                  </Link>
                ))}
              </div>
            )}

            <div className="mt-5">
              <Link
                to={`/${latest.page.slug}`}
                className="ecosystem-cta-button inline-flex items-center justify-center !h-10 px-5 text-sm font-bold
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-calamity-accent/50"
              >
                Read full notes &rarr;
              </Link>
            </div>
          </section>

          {/* Timeline */}
          {visibleGroups.map(group => (
            <MonthGroup key={group.label} group={group} />
          ))}

          {olderGroups.length > 0 && !showOlder && (
            <button
              type="button"
              onClick={() => setShowOlder(true)}
              className="mt-6 flex w-full items-center gap-3 rounded-xl border border-dashed border-calamity-border-subtle/80 px-4 py-3
                         text-sm text-calamity-text-muted transition-colors hover:border-calamity-accent hover:text-calamity-text
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-calamity-accent/50"
            >
              <span className="rounded-full bg-calamity-panel-hover px-2.5 py-0.5 text-xs font-bold text-calamity-text">
                {olderCount}
              </span>
              Show earlier patches
              <span className="ml-auto" aria-hidden="true">&darr;</span>
            </button>
          )}

          {showOlder && olderGroups.map(group => (
            <MonthGroup key={group.label} group={group} />
          ))}
        </div>
      </article>

      {/* Right rail: recent versions + month jumps (same shell as the article TOC) */}
      <aside className="hidden min-[1280px]:block w-52 shrink-0 sticky top-[var(--wiki-sticky-top)] self-start max-h-[var(--wiki-sticky-max-h)] overflow-y-auto pl-4 border-l border-calamity-border-subtle/25">
        <h4 className="text-[10px] uppercase tracking-[0.15em] text-calamity-text-muted font-bold mb-3 px-1">
          Versions
        </h4>
        <nav className="space-y-0.5">
          {timeline.slice(0, 10).map((entry, idx) => (
            <Link
              key={entry.page.id}
              to={`/${entry.page.slug}`}
              className={`
                flex items-baseline justify-between gap-2 text-xs py-1.5 pl-3 pr-2 border-l-2 rounded-md transition-colors
                ${idx === 0
                  ? 'border-calamity-accent text-calamity-text bg-calamity-accent/10'
                  : 'border-transparent text-calamity-text-muted hover:text-calamity-text-dim hover:bg-calamity-panel-hover/25'
                }
              `}
            >
              <span className="font-semibold tabular-nums">{entry.version ?? entry.page.title}</span>
              <span className="text-[10px] tabular-nums opacity-80">{entry.dateLabel}</span>
            </Link>
          ))}
        </nav>

        <div className="h-px bg-calamity-border-subtle/40 my-3" aria-hidden="true" />

        <h4 className="text-[10px] uppercase tracking-[0.15em] text-calamity-text-muted font-bold mb-3 px-1">
          Jump to
        </h4>
        <nav className="space-y-0.5">
          {monthGroups.map((group, idx) => (
            <button
              key={group.label}
              type="button"
              onClick={() => jumpToMonth(group.label, idx >= EXPANDED_MONTHS)}
              className="flex w-full items-baseline justify-between gap-2 text-xs py-1.5 pl-3 pr-2 border-l-2 border-transparent rounded-md text-left
                         text-calamity-text-muted transition-colors hover:text-calamity-text-dim hover:bg-calamity-panel-hover/25
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-calamity-accent/50"
            >
              <span>{group.label}</span>
              <span className="text-[10px] tabular-nums opacity-80">{group.entries.length}</span>
            </button>
          ))}
        </nav>
      </aside>
    </div>
  )
}

function MonthGroup({ group }: { group: { label: string; entries: PatchInfo[] } }) {
  return (
    <section id={monthId(group.label)} aria-label={group.label} className="mt-6 first-of-type:mt-0">
      <div className="mb-2 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.14em] text-calamity-text-muted">
        {group.label}
        <span className="h-px flex-1 bg-calamity-border-subtle/60" aria-hidden="true" />
      </div>
      {group.entries.map(entry => (
        <PatchRow key={entry.page.id} entry={entry} />
      ))}
    </section>
  )
}

function PatchRow({ entry }: { entry: PatchInfo }) {
  const summary = entry.page.meta_description || ''
  return (
    <Link
      to={`/${entry.page.slug}`}
      className="grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-baseline gap-3 rounded-lg px-3 py-2.5
                 transition-colors hover:bg-calamity-panel-hover/40
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-calamity-accent/50"
    >
      <span className="font-display text-lg leading-none text-[#ffb04a] tabular-nums">
        {entry.version ?? '—'}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm text-calamity-text-muted">{summary}</span>
      </span>
      <span className="text-xs text-calamity-text-muted/80 tabular-nums">{entry.dateLabel}</span>
    </Link>
  )
}
